import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, Mail, ArrowLeft, Pencil, Clock, Play, Pause, CheckSquare, UserCheck, Megaphone, GitMerge, Star, FileText, Copy, Building2, Plus, Search, Unlink, Trash2, RotateCcw, Shield, MessageSquare, ArrowRight, Filter, ChevronDown, ChevronUp, Lock, Unlock, Upload } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { leadsApi, tasksApi, emailApi, usersApi, companiesApi, playbookApi } from '../../api/endpoints'
import ScoreCard from '../../components/leads/ScoreCard'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Input, { Select, Textarea } from '../../components/ui/Input'
import PhoneInput from '../../components/ui/PhoneInput'
import Badge, { StageBadge, SegmentBadge, StatusBadge, PriorityBadge } from '../../components/ui/Badge'
import { fmtDateTime, fmtDate, fmtRelative, istInputToUTC, toISTInputValue } from '../../utils/date'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import CallPulseModal from '../../components/shared/CallPulseModal'
import ScrollableTabs from '../../components/ui/ScrollableTabs'
import { usePipelineStages } from '../../hooks/usePipelineStages'

const FALLBACK_PIPELINE_STAGES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'demo_scheduled', label: 'Demo' },
  { value: 'proposal_sent', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'overview' | 'calls' | 'emails' | 'tasks' | 'submissions'>('overview')
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [transcriptId, setTranscriptId] = useState<number | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [overviewSubView, setOverviewSubView] = useState<'timeline' | 'details'>('timeline')
  const [timelineFilter, setTimelineFilter] = useState<'all' | 'call' | 'note' | 'callpulse' | 'stage'>('all')
  const [timelineCount, setTimelineCount] = useState(15)
  const [callsOffset, setCallsOffset] = useState(0)
  const [emailsPage, setEmailsPage] = useState(0)
  const [tasksPage, setTasksPage] = useState(0)
  const [submissionsPage, setSubmissionsPage] = useState(0)

  const authUser = useAuthStore((s) => s.user)

  const stagesFromApi = usePipelineStages()
  const PIPELINE_STAGES = stagesFromApi.length > 0
    ? stagesFromApi.map(s => ({ value: s.slug, label: s.label }))
    : FALLBACK_PIPELINE_STAGES

  const leadRef = id || '' // public lead_id (LD-XXXXXXXX) or integer string for backward compat

  const { data, isLoading } = useQuery({
    queryKey: ['lead', leadRef],
    queryFn: () => leadsApi.get(leadRef),
    enabled: !!leadRef,
  })

  const { data: emailsData } = useQuery({
    queryKey: ['lead-emails', leadRef],
    queryFn: () => emailApi.leadEmails(leadRef),
    enabled: !!leadRef,
  })

  const { data: tasksData } = useQuery({
    queryKey: ['lead-tasks', leadRef],
    queryFn: () => tasksApi.list({ lead_id: leadRef }),
    enabled: !!leadRef,
  })

  const { data: assignmentsData } = useQuery({
    queryKey: ['lead-assignments', leadRef],
    queryFn: () => leadsApi.getAssignments(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const { data: submissionsData } = useQuery({
    queryKey: ['lead-submissions', leadRef],
    queryFn: () => leadsApi.getSubmissions(leadRef),
    enabled: !!leadRef,
  })

  const { data: contactEmailsData } = useQuery({
    queryKey: ['lead-contact-emails', leadRef],
    queryFn: () => leadsApi.getEmails(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const { data: contactPhonesData } = useQuery({
    queryKey: ['lead-contact-phones', leadRef],
    queryFn: () => leadsApi.getPhones(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const { data: mergeHistoryData } = useQuery({
    queryKey: ['lead-merge-history', leadRef],
    queryFn: () => leadsApi.getMergeHistory(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const { data: pulseResponsesData } = useQuery({
    queryKey: ['lead-pulse-responses', leadRef],
    queryFn: () => playbookApi.listResponses(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const { data: notesData } = useQuery({
    queryKey: ['lead-notes', leadRef],
    queryFn: () => leadsApi.getNotes(leadRef),
    enabled: !!leadRef && activeTab === 'overview',
  })

  const stageMutation = useMutation({
    mutationFn: (stage: string) => leadsApi.updateStage(leadRef, stage),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead', leadRef] }),
  })

  const unmergeMutation = useMutation({
    mutationFn: (snapshotId: number) => leadsApi.unmergeLead(leadRef, { snapshot_id: snapshotId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['lead-merge-history', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['lead-contact-emails', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['lead-contact-phones', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['lead-submissions', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })

  const segmentLockMutation = useMutation({
    mutationFn: ({ locked, segment }: { locked: boolean; segment: string }) =>
      leadsApi.update(leadRef, { segment_locked: locked, segment_override: segment }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lead', leadRef] }),
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lead = data?.data as any

  const stageHistory = (lead?.stage_history as Array<Record<string, string>>) || []
  const callLogs: any[] = lead?.call_logs || []
  const assignments: any[] = assignmentsData?.data || []
  const mergeHistory: any[] = mergeHistoryData?.data || []
  const notes: any[] = notesData?.data || []
  const pulseResponses: any[] = pulseResponsesData?.data || []

  // Build unified timeline items (must be before early returns — Rules of Hooks)
  const timelineItems = useMemo(() => {
    const items: Array<{
      type: 'call' | 'note' | 'callpulse' | 'stage'
      date: string
      title: string
      detail: string
      actor: string
      icon: typeof Phone
      color: string
      borderColor: string
      raw: any
    }> = []

    // Call logs
    for (const c of callLogs) {
      const dur = c.duration_minutes != null ? ` (${c.duration_minutes} min)` : ''
      const dir = c.direction ? ` ${c.direction}` : ''
      const isVoip = c.source === 'voip'
      items.push({
        type: 'call',
        date: c.call_date || c.created_at,
        title: `${isVoip ? 'VoIP' : 'Manual'}${dir} call - ${String(c.outcome || 'Unknown').replace(/_/g, ' ')}${dur}`,
        detail: c.summary || '',
        actor: c.created_by_name || '',
        icon: isVoip ? Phone : Pencil,
        color: isVoip ? 'text-blue-600' : 'text-gray-500',
        borderColor: isVoip ? 'border-l-blue-400' : 'border-l-gray-300',
        raw: c,
      })
    }

    // Notes
    for (const n of notes) {
      const isMeta = n.source === 'meta_lead_form'
      items.push({
        type: 'note',
        date: n.created_at,
        title: isMeta ? 'Meta Form Submission' : 'Note added',
        detail: n.content || '',
        actor: n.created_by_name || (isMeta ? 'Meta Ads' : 'System'),
        icon: isMeta ? Megaphone : FileText,
        color: isMeta ? 'text-blue-600' : 'text-amber-600',
        borderColor: isMeta ? 'border-l-blue-400' : 'border-l-amber-400',
        raw: n,
      })
    }

    // CallPulse responses
    for (const rs of pulseResponses) {
      items.push({
        type: 'callpulse',
        date: rs.submitted_at || rs.created_at,
        title: `CallPulse: ${rs.playbook_name || 'Questionnaire'}`,
        detail: rs.status === 'draft' ? 'Draft - in progress' : 'Submitted',
        actor: '',
        icon: MessageSquare,
        color: 'text-purple-600',
        borderColor: 'border-l-purple-400',
        raw: rs,
      })
    }

    // Stage changes
    for (const h of stageHistory) {
      const from = h.from_stage ? String(h.from_stage).replace(/_/g, ' ') : 'Created'
      const to = String(h.to_stage).replace(/_/g, ' ')
      items.push({
        type: 'stage',
        date: h.changed_at,
        title: `Stage: ${from} to ${to}`,
        detail: '',
        actor: h.changed_by_name || '',
        icon: ArrowRight,
        color: 'text-green-600',
        borderColor: 'border-l-green-400',
        raw: h,
      })
    }

    // Sort by date DESC
    items.sort((a, b) => {
      const da = new Date(a.date || 0).getTime()
      const db = new Date(b.date || 0).getTime()
      return db - da
    })

    return items
  }, [callLogs, notes, pulseResponses, stageHistory])

  const filteredTimeline = useMemo(() => {
    if (timelineFilter === 'all') return timelineItems
    return timelineItems.filter(item => item.type === timelineFilter)
  }, [timelineItems, timelineFilter])

  const timeAgo = (dateStr: string) => fmtRelative(dateStr)

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>
  if (!lead) return <div className="p-8 text-center text-gray-500">Lead not found</div>

  const handleCall = () => {
    navigate(`/voip?lead=${leadRef}`)
  }

  return (
    <div>
      {/* Back button */}
      <button onClick={() => navigate('/leads')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} /> Back to Leads
      </button>

      {/* Merged banner */}
      {lead.merged_into_id && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-amber-600" />
            <span className="text-sm text-amber-800">This lead has been merged into Lead {(lead as any).merged_into_public_id || `#${lead.merged_into_id}`}</span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/leads/${(lead as any).merged_into_public_id || lead.merged_into_id}`)}>
            View Merged Lead
          </Button>
        </div>
      )}

      {(() => {
        const duplicateSubmissions = (submissionsData?.data || []).filter((s: any) => s.is_duplicate)
        return duplicateSubmissions.length > 0 ? (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
            <Copy size={16} className="text-orange-600 flex-shrink-0" />
            <div>
              <span className="text-sm font-medium text-orange-800">
                {duplicateSubmissions.length} duplicate submission{duplicateSubmissions.length > 1 ? 's' : ''} detected
              </span>
              <span className="text-xs text-orange-600 ml-2">
                This lead was submitted multiple times. View the Submissions tab for details.
              </span>
            </div>
          </div>
        ) : null
      })()}

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{lead.name as string}</h1>
              <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{(lead as any).lead_id || leadRef}</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mt-1 text-sm text-gray-500 flex-wrap">
              {lead.title && <span>{String(lead.title)}</span>}
              {lead.company && <span>at {String(lead.company)}</span>}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mt-2 flex-wrap">
              <StageBadge stage={lead.stage as string} />
              <SegmentBadge segment={lead.quality_segment as string | null} />
              <button
                onClick={() => segmentLockMutation.mutate({
                  locked: !lead.segment_locked,
                  segment: (lead.segment_override || lead.quality_segment || 'COLD') as string,
                })}
                className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
                title={lead.segment_locked ? 'Segment locked — click to unlock' : 'Click to lock segment'}
                disabled={segmentLockMutation.isPending}
              >
                {lead.segment_locked ? <Lock size={14} /> : <Unlock size={14} className="opacity-40 hover:opacity-100" />}
              </button>
              {lead.channel && (
                <Badge variant="default">{(lead.channel as string).replace(/_/g, ' ')}</Badge>
              )}
              {(lead as any).lead_source === 'instagram_discovery' && (
                <a href="/ig-discovery" className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded hover:bg-purple-100 transition-colors">
                  From IG Discovery
                </a>
              )}
              {lead.score != null && (
                <span className="text-sm font-semibold text-gray-700">{Math.round(lead.score as number)} pts</span>
              )}
            </div>
          </div>
          {/* Mobile: large action buttons in a row */}
          <div className="flex items-center gap-2 sm:gap-2 flex-wrap">
            {lead.phone_number && (
              <Button onClick={handleCall} size="sm">
                <Phone size={14} /> Call
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setEditModalOpen(true)}>
              <Pencil size={14} /> <span className="hidden sm:inline">Edit</span>
            </Button>
            {authUser?.role === 'admin' && (
              <Button variant="secondary" size="sm" onClick={() => setMergeModalOpen(true)}>
                <GitMerge size={14} /> <span className="hidden sm:inline">Merge</span>
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setTaskModalOpen(true)}>
              <Plus size={14} /> <span className="hidden sm:inline">Add Task</span>
            </Button>
          </div>
        </div>

        {/* Contact info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div><p className="text-xs text-gray-500">Email</p><p className="text-sm">{(lead.email as string) || '\u2014'}</p></div>
          <div><p className="text-xs text-gray-500">Phone</p><p className="text-sm">{(lead.phone_number as string) || '\u2014'}</p></div>
          <div><p className="text-xs text-gray-500">Industry</p><p className="text-sm">{(lead.industry as string) || '\u2014'}</p></div>
          <div><p className="text-xs text-gray-500">Est. Value</p><p className="text-sm">{lead.deal_value ? `\u20B9${(lead.deal_value as number).toLocaleString()}` : '\u2014'}</p></div>
          {lead.submitted_at && (
            <div><p className="text-xs text-gray-500">Submitted</p><p className="text-sm">{fmtDateTime(lead.submitted_at as string)}</p></div>
          )}
        </div>

        {/* Attribution Info */}
        {lead.meta_campaign_id && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Megaphone size={14} className="text-blue-600" />
              <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Source: Meta Ads</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[11px] text-blue-500">Campaign</p>
                <p className="text-blue-900">{String(lead.meta_campaign_name || lead.meta_campaign_id)}</p>
              </div>
              {lead.meta_adset_id && (
                <div>
                  <p className="text-[11px] text-blue-500">Ad Set</p>
                  <p className="text-blue-900">{String(lead.meta_adset_id)}</p>
                </div>
              )}
              {lead.meta_ad_id && (
                <div>
                  <p className="text-[11px] text-blue-500">Ad</p>
                  <p className="text-blue-900">{String(lead.meta_ad_id)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignment Info */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-500">Assigned to</p>
            <p className="text-sm">{lead.owner_name || lead.owner ? String(lead.owner_name || lead.owner) : <span className="text-gray-400">Unassigned</span>}</p>
          </div>
          {lead.assigned_at && (
            <div>
              <p className="text-xs text-gray-500">Assigned</p>
              <p className="text-sm">{timeAgo(String(lead.assigned_at))}</p>
            </div>
          )}
          {lead.assignment_strategy && (
            <div>
              <p className="text-xs text-gray-500">Strategy</p>
              <p className="text-sm capitalize">{String(lead.assignment_strategy).replace('_', ' ')}</p>
            </div>
          )}
          {lead.sla_status && (
            <div>
              <p className="text-xs text-gray-500">SLA</p>
              <div className="flex items-center gap-1.5">
                <span className={`inline-block w-2 h-2 rounded-full ${
                  lead.sla_status === 'ok' ? 'bg-green-500' :
                  lead.sla_status === 'warning' ? 'bg-amber-500' :
                  lead.sla_status === 'breached' ? 'bg-red-500' : 'bg-gray-300'
                }`} />
                <span className="text-sm capitalize">{String(lead.sla_status)}</span>
                {lead.sla_deadline && <span className="text-xs text-gray-400 ml-1">by {fmtDateTime(lead.sla_deadline)}</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Stage Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-xs font-medium text-gray-500 uppercase mb-3">Pipeline Stage</h3>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {PIPELINE_STAGES.map((stage, i) => {
            const isCurrent = lead.stage === stage.value
            const currentIdx = PIPELINE_STAGES.findIndex(s => s.value === lead.stage)
            const isPast = i < currentIdx
            const isLost = stage.value === 'lost'
            return (
              <button
                key={stage.value}
                onClick={() => { if (!isCurrent) stageMutation.mutate(stage.value) }}
                disabled={stageMutation.isPending}
                className={`flex-1 py-2 px-1 text-xs font-medium rounded-lg text-center transition-colors ${
                  isCurrent
                    ? isLost ? 'bg-red-500 text-white' : 'bg-indigo-500 text-white'
                    : isPast
                    ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    : isLost
                    ? 'bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-600'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {stage.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Score Breakdown — visual score card */}
      <ScoreCard lead={lead as Record<string, unknown>} />

      {/* Scoring Inputs from CallPulse */}
      <ScoringInputsSection lead={lead} />

      {/* Tabs with record counts */}
      {(() => {
        const tabCounts: Record<string, number | undefined> = {
          overview: undefined,
          calls: callLogs.length || undefined,
          emails: (emailsData?.total ?? emailsData?.emails?.length) || undefined,
          tasks: (tasksData?.total ?? tasksData?.tasks?.length) || undefined,
          submissions: submissionsData?.data?.length || undefined,
        }
        return (
          <ScrollableTabs
            tabs={(['overview', 'calls', 'emails', 'tasks', 'submissions'] as const).map(tab => ({
              key: tab,
              label: tab.charAt(0).toUpperCase() + tab.slice(1),
              count: tabCounts[tab],
            }))}
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as typeof activeTab)}
            variant="pill"
            className="mb-4"
          />
        )
      })()}

      {/* Overview sub-view counts */}
      {activeTab === 'overview' && (
        <div className="flex items-center gap-3 mb-3 text-xs text-gray-400">
          <span>{callLogs.length} call log{callLogs.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-300">|</span>
          <span>{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-300">|</span>
          <span>{pulseResponses.length} CallPulse response{pulseResponses.length !== 1 ? 's' : ''}</span>
          <span className="text-gray-300">|</span>
          <span>{stageHistory.length} stage change{stageHistory.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {/* Sub-view pills */}
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setOverviewSubView('timeline')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  overviewSubView === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setOverviewSubView('details')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  overviewSubView === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Details
              </button>
            </div>
          </div>

          {/* Company Linking — always visible */}
          <CompanyLinkingSection lead={lead} leadRef={leadRef} />

          {/* ===== TIMELINE SUB-VIEW ===== */}
          {overviewSubView === 'timeline' && (
            <div className="space-y-3">
              {/* Filter bar */}
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-400" />
                {(['all', 'call', 'note', 'callpulse', 'stage'] as const).map(f => {
                  const labels: Record<string, string> = { all: 'All', call: 'Calls', note: 'Notes', callpulse: 'CallPulse', stage: 'Stage Changes' }
                  return (
                    <button
                      key={f}
                      onClick={() => { setTimelineFilter(f); setTimelineCount(15) }}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        timelineFilter === f
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {labels[f]}
                    </button>
                  )
                })}
              </div>

              {/* Timeline feed */}
              {filteredTimeline.length === 0 ? (
                <EmptyState icon={Clock} title="No activity yet" description="Calls, notes, stage changes, and CallPulse responses will appear here as a single feed" />
              ) : (
                <div className="space-y-2">
                  {filteredTimeline.slice(0, timelineCount).map((item, idx) => (
                    <TimelineCard key={`${item.type}-${idx}`} item={item} />
                  ))}
                  {filteredTimeline.length > timelineCount && (
                    <button
                      onClick={() => setTimelineCount(c => c + 15)}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      Show more ({filteredTimeline.length - timelineCount} remaining)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== DETAILS SUB-VIEW ===== */}
          {overviewSubView === 'details' && (
            <div className="space-y-4">
              {/* Stage History */}
              {stageHistory.length > 0 && (
                <PaginatedSection title="Stage History" items={stageHistory} limit={5} renderItem={(h, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      {h.from_stage ? (
                        <StageBadge stage={h.from_stage} />
                      ) : (
                        <span className="text-xs text-gray-400 italic">Created</span>
                      )}
                      <span className="text-gray-400">&rarr;</span>
                      <StageBadge stage={h.to_stage} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {h.changed_by_name && (
                        <span className="text-gray-500">{h.changed_by_name}</span>
                      )}
                      <span className="text-gray-300">&middot;</span>
                      <span>{fmtDateTime(h.changed_at)}</span>
                    </div>
                  </div>
                )} />
              )}

              {/* Assignment History */}
              {assignments.length > 0 && (
                <PaginatedSection title="Assignment History" items={assignments} limit={5} renderItem={(a: any, i: number) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <UserCheck size={14} className="text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-900 font-medium">
                          {a.assigned_from ? 'Reassigned' : 'Assigned'} to {a.assigned_to_name || 'Unknown'}
                        </span>
                        {a.strategy_used && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-medium capitalize">
                            {String(a.strategy_used).replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{a.created_at ? fmtDateTime(a.created_at) : ''}</span>
                        <span className="text-xs text-gray-400">({a.created_at ? timeAgo(a.created_at) : ''})</span>
                      </div>
                      {a.reason && (
                        <p className="text-xs text-gray-500 mt-0.5">Reason: {a.reason}</p>
                      )}
                    </div>
                  </div>
                )} />
              )}

              {/* Merge History */}
              {mergeHistory.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Merge History</h3>
                  <div className="space-y-3">
                    {mergeHistory.map((item: any, i: number) => {
                      const isActive = item.is_active === true
                      const isUnmerge = item.action === 'unmerge'
                      return (
                        <div key={item.id || i} className="flex items-start gap-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isUnmerge ? 'bg-gray-100' : 'bg-purple-50'
                          }`}>
                            <GitMerge size={14} className={isUnmerge ? 'text-gray-500' : 'text-purple-600'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-gray-900 font-medium">
                                {isUnmerge
                                  ? `Unmerge: Lead "${item.merged_lead_name || `#${item.merged_lead_id}`}" separated`
                                  : `Lead "${item.merged_lead_name || `#${item.merged_lead_id}`}" merged into this lead`
                                }
                              </span>
                              {isUnmerge && (
                                <Badge variant="default">Reversed</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{item.created_at ? fmtDateTime(item.created_at) : ''}</span>
                              <span className="text-xs text-gray-400">({item.created_at ? timeAgo(item.created_at) : ''})</span>
                            </div>
                            {isActive && !isUnmerge && authUser?.role === 'admin' && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`This will reverse the merge and restore Lead "${item.merged_lead_name || `#${item.merged_lead_id}`}" as a separate lead. Continue?`)) {
                                    unmergeMutation.mutate(item.id)
                                  }
                                }}
                                disabled={unmergeMutation.isPending}
                                className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors disabled:opacity-50"
                              >
                                Unmerge
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Call Logs (immutable) */}
              <ManualCallLogSection leadRef={leadRef} callLogs={callLogs} />

              {/* Notes (immutable, append-only) */}
              <LeadNotesSection leadRef={leadRef} />

              {/* CallPulse Responses */}
              <CallPulseResponsesSection responses={pulseResponsesData?.data || []} leadRef={leadRef} />

              {/* Edit History (admin/manager only) */}
              {(authUser?.role === 'admin' || authUser?.role === 'manager') && (
                <EditHistorySection leadRef={leadRef} />
              )}

              {/* All Emails */}
              {(contactEmailsData?.data || []).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">All Email Addresses</h3>
                  <div className="space-y-2">
                    {(contactEmailsData?.data || []).map((em: any) => (
                      <ContactEmailRow key={em.id} email={em} leadId={leadRef} />
                    ))}
                  </div>
                </div>
              )}

              {/* All Phones */}
              {(contactPhonesData?.data || []).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">All Phone Numbers</h3>
                  <div className="space-y-2">
                    {(contactPhonesData?.data || []).map((ph: any) => (
                      <ContactPhoneRow key={ph.id} phone={ph} leadId={leadRef} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'calls' && (() => {
        const allCalls: any[] = callLogs
        const CALLS_LIMIT = 10
        const pagedCalls = allCalls.slice(callsOffset, callsOffset + CALLS_LIMIT)
        return (
          <div className="bg-white rounded-xl border border-gray-200">
            {allCalls.length === 0 ? (
              <div className="p-8"><EmptyState icon={Phone} title="No calls" description="Call this lead to see history here" /></div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {pagedCalls.map((call: any) => {
                    const isVoip = call.source === 'voip'
                    const durStr = call.duration_seconds != null
                      ? `${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}`
                      : call.duration_minutes != null ? `${call.duration_minutes} min` : null
                    return (
                    <div key={call.id}>
                      <div className="px-6 py-4 flex items-center gap-4">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${call.direction === 'inbound' ? 'bg-blue-50' : 'bg-green-50'}`}>
                          {isVoip ? (
                            <Phone size={16} className={call.direction === 'inbound' ? 'text-blue-600' : 'text-green-600'} />
                          ) : (
                            <Pencil size={16} className="text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={call.outcome || 'unknown'} />
                            <span className="text-xs text-gray-400 capitalize">{call.direction || ''}</span>
                            {isVoip && call.provider && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-medium">{call.provider}</span>}
                            {!isVoip && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-medium">Manual</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                            <span>{fmtDateTime(call.call_date || call.created_at)}</span>
                            {durStr && <span><Clock size={10} className="inline" /> {durStr}</span>}
                            {call.created_by_name && <span className="text-gray-400">by {call.created_by_name}</span>}
                            {call.call_quality_score && (
                              <span className="text-amber-500 text-[10px]">{'★'.repeat(call.call_quality_score)}</span>
                            )}
                          </div>
                          {call.summary && (
                            <p className="text-xs text-gray-500 mt-0.5">{call.summary}</p>
                          )}
                          {call.next_action && (
                            <p className="text-xs text-indigo-500 mt-0.5">Next: {call.next_action}</p>
                          )}
                          {!isVoip && !call.voip_call_id && !call.has_manual_recording && (
                            <AddCallRecording leadRef={leadRef} callId={call.id} />
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {lead.phone_number && (
                            <button onClick={handleCall} className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg" title="Call">
                              <Phone size={15} />
                            </button>
                          )}
                          {(call.transcript_status === 'completed' || call.transcript_status === 'skipped') && (
                            <button
                              onClick={() => setTranscriptId(transcriptId === call.id ? null : call.id)}
                              className={`p-2 rounded-lg transition-colors ${transcriptId === call.id ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-100'}`}
                              title="View transcript"
                            >
                              <FileText size={16} />
                            </button>
                          )}
                          {((call.recording_url && call.voip_call_id) || call.has_manual_recording) && (
                            <button
                              onClick={() => setPlayingId(playingId === call.id ? null : call.id)}
                              className={`p-2 rounded-lg transition-colors ${playingId === call.id ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-100'}`}
                              title="Play recording"
                            >
                              {playingId === call.id ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                          )}
                        </div>
                      </div>
                      {playingId === call.id && (call.voip_call_id || call.has_manual_recording) && (
                        <div className="px-6 pb-4">
                          <audio
                            controls
                            autoPlay
                            className="w-full h-8"
                            src={call.voip_call_id ? `/api/voip/call/${call.voip_call_id}/recording` : `/api/leads/${leadRef}/calls/${call.id}/recording`}
                            onEnded={() => setPlayingId(null)}
                          />
                        </div>
                      )}
                      {transcriptId === call.id && (
                        <CallTranscript
                          path={call.voip_call_id ? `/voip/call/${call.voip_call_id}/transcript` : `/leads/${leadRef}/calls/${call.id}/transcript`}
                        />
                      )}
                    </div>
                  )})}
                </div>
                <div className="px-6 pb-4">
                  <Pagination total={allCalls.length} limit={CALLS_LIMIT} offset={callsOffset} onChange={setCallsOffset} />
                </div>
              </>
            )}
          </div>
        )
      })()}

      {activeTab === 'emails' && (() => {
        const allEmails = emailsData?.emails || []
        const EMAILS_LIMIT = 10
        const emailStart = emailsPage * EMAILS_LIMIT
        const pagedEmails = allEmails.slice(emailStart, emailStart + EMAILS_LIMIT)
        return (
          <div className="bg-white rounded-xl border border-gray-200">
            {allEmails.length === 0 ? (
              <div className="p-8"><EmptyState icon={Mail} title="No emails sent" description="Send an email from the Email page" /></div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {pagedEmails.map((e) => (
                    <div key={e.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">{e.subject}</p>
                        <StatusBadge status={e.status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span>To: {e.to_address}</span>
                        <span>{fmtDateTime(e.sent_at || e.created_at)}</span>
                        {e.open_count > 0 && <span className="text-green-600">Opened {e.open_count}x</span>}
                        {e.click_count > 0 && <span className="text-blue-600">Clicked {e.click_count}x</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-4">
                  <Pagination total={allEmails.length} limit={EMAILS_LIMIT} offset={emailStart} onChange={(o) => setEmailsPage(Math.floor(o / EMAILS_LIMIT))} />
                </div>
              </>
            )}
          </div>
        )
      })()}

      {activeTab === 'tasks' && (() => {
        const allTasks = tasksData?.tasks || []
        const TASKS_LIMIT = 10
        const taskStart = tasksPage * TASKS_LIMIT
        const pagedTasks = allTasks.slice(taskStart, taskStart + TASKS_LIMIT)
        return (
          <div className="bg-white rounded-xl border border-gray-200">
            {allTasks.length === 0 ? (
              <div className="p-8"><EmptyState icon={CheckSquare} title="No tasks" description="Create a task linked to this lead" /></div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {pagedTasks.map((t) => (
                    <div key={t.id} className="px-6 py-4 flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${t.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>{t.title}</p>
                          <PriorityBadge priority={t.priority} />
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span className="capitalize">{t.task_type.replace('_', ' ')}</span>
                          {t.due_date && <span>Due: {fmtDate(t.due_date)}</span>}
                          {t.assigned_user_name && <span>&rarr; {t.assigned_user_name}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-4">
                  <Pagination total={allTasks.length} limit={TASKS_LIMIT} offset={taskStart} onChange={(o) => setTasksPage(Math.floor(o / TASKS_LIMIT))} />
                </div>
              </>
            )}
          </div>
        )
      })()}

      {activeTab === 'submissions' && (() => {
        const allSubs = submissionsData?.data || []
        const SUBS_LIMIT = 10
        const subStart = submissionsPage * SUBS_LIMIT
        const pagedSubs = allSubs.slice(subStart, subStart + SUBS_LIMIT)
        return (
          <div className="bg-white rounded-xl border border-gray-200">
            {allSubs.length === 0 ? (
              <div className="p-8"><EmptyState icon={FileText} title="No submissions" description="Form submissions and imports will appear here" /></div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {pagedSubs.map((sub: any, idx: number) => (
                    <div key={sub.id || idx} className="px-6 py-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Clock size={14} className="text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">
                              {sub.source === 'meta_webhook' ? 'Form Submission' : sub.source === 'csv_import' ? 'CSV Import' : String(sub.source || 'Submission')}
                            </span>
                            <span className="text-xs text-gray-400">{String(sub.source || '')}</span>
                            {sub.created_at && (
                              <span className="text-xs text-gray-500">{fmtDateTime(sub.created_at)}</span>
                            )}
                          </div>
                        </div>
                        {sub.is_duplicate ? (
                          <Badge variant="danger">Duplicate</Badge>
                        ) : (
                          <Badge variant="success">Original</Badge>
                        )}
                      </div>
                      <div className="ml-11 text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-4 flex-wrap">
                          {sub.email && <span>Email: {String(sub.email)}</span>}
                          {sub.phone && <span>Phone: {String(sub.phone)}</span>}
                        </div>
                        <div className="flex items-center gap-4 flex-wrap text-xs text-gray-400">
                          {sub.form_id && <span>Form: {String(sub.form_id)}</span>}
                          {sub.campaign_id && <span>Campaign: {String(sub.campaign_id)}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-4">
                  <Pagination total={allSubs.length} limit={SUBS_LIMIT} offset={subStart} onChange={(o) => setSubmissionsPage(Math.floor(o / SUBS_LIMIT))} />
                </div>
              </>
            )}
          </div>
        )
      })()}

      {/* Edit Modal */}
      <LeadEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        lead={lead}
        leadId={leadRef}
      />

      {/* Merge Modal */}
      <MergeLeadModal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        leadId={leadRef}
        leadName={lead.name as string}
      />

      {/* Quick Task Modal */}
      <LeadTaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        leadId={lead.id as number}
      />

      {/* CallPulse auto-open removed — calls are now initiated from VoIP page only */}
    </div>
  )
}

function LeadEditModal({ open, onClose, lead, leadId }: { open: boolean; onClose: () => void; lead: any; leadId: string | number }) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '', email: '', phone_number: '', company: '', title: '',
    industry: '', website: '', stage: 'new', notes: '', deal_value: '', owner_id: '',
  })
  const [error, setError] = useState('')

  // Fetch users for owner selector
  const { data: usersData } = useQuery({
    queryKey: ['users-for-owner'],
    queryFn: () => usersApi.list(),
    enabled: open,
  })
  const userOptions = [
    { value: '', label: 'Unassigned' },
    ...((usersData?.users || []).filter((u: any) => u.is_active).map((u: any) => ({
      value: String(u.id),
      label: u.full_name || u.username,
    }))),
  ]

  // Sync form when lead data loads
  const [syncedId, setSyncedId] = useState<string | number | null>(null)
  if (open && lead && syncedId !== leadId) {
    setSyncedId(leadId)
    setForm({
      name: lead.name || '',
      email: lead.email || '',
      phone_number: lead.phone_number || '',
      company: lead.company || '',
      title: lead.title || '',
      industry: lead.industry || '',
      website: lead.website || '',
      stage: lead.stage || 'new',
      notes: lead.notes || '',
      deal_value: lead.deal_value != null ? String(lead.deal_value) : '',
      owner_id: lead.owner_id != null ? String(lead.owner_id) : '',
    })
    setError('')
  }

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => leadsApi.update(leadId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-assignments', leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const assignMutation = useMutation({
    mutationFn: (userId: number) =>
      leadsApi.assignLead(leadId, { assigned_to: userId, reason: 'Reassigned via lead detail edit' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-assignments', leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: Record<string, unknown> = { ...form }
    if (form.deal_value) data.deal_value = parseFloat(form.deal_value)
    else delete data.deal_value

    const newOwnerId = form.owner_id ? parseInt(form.owner_id) : null
    const oldOwnerId = lead?.owner_id || null
    delete data.owner_id

    Object.keys(data).forEach(k => { if (data[k] === '') delete data[k] })

    updateMutation.mutate(data, {
      onSuccess: () => {
        if (newOwnerId && newOwnerId !== oldOwnerId) {
          assignMutation.mutate(newOwnerId)
        }
      },
    })
  }

  if (!lead) return null

  const STAGE_OPTS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'demo_scheduled', label: 'Demo Scheduled' },
    { value: 'proposal_sent', label: 'Proposal Sent' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
  ]

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${lead.name}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
          <PhoneInput label="Phone" value={form.phone_number} onChange={(val) => setForm(f => ({ ...f, phone_number: val }))} />
          <Input label="Company" value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} />
          <Input label="Title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
          <Input label="Industry" value={form.industry} onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))} />
          <Input label="Website" value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} />
          <Input label="Est. Contract Value (INR)" type="number" value={form.deal_value} onChange={(e) => setForm(f => ({ ...f, deal_value: e.target.value }))} />
          <Select label="Stage" options={STAGE_OPTS} value={form.stage} onChange={(e) => setForm(f => ({ ...f, stage: e.target.value }))} />
          <Select label="Assigned To" options={userOptions} value={form.owner_id} onChange={(e) => setForm(f => ({ ...f, owner_id: e.target.value }))} />
        </div>
        <Textarea label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={updateMutation.isPending}>Save Changes</Button>
        </div>
      </form>
    </Modal>
  )
}

function ContactEmailRow({ email, leadId }: { email: any; leadId: string | number }) {
  const queryClient = useQueryClient()
  const setPrimaryMutation = useMutation({
    mutationFn: () => leadsApi.setPrimaryEmail(leadId, { email_id: email.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-contact-emails', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
    },
  })

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-gray-400" />
        <span className="text-sm text-gray-700">{String(email.email)}</span>
        {email.is_primary && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded">
            <Star size={10} className="fill-amber-500 text-amber-500" /> Primary
          </span>
        )}
      </div>
      {!email.is_primary && (
        <button
          onClick={() => setPrimaryMutation.mutate()}
          disabled={setPrimaryMutation.isPending}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
        >
          Set Primary
        </button>
      )}
    </div>
  )
}

function ContactPhoneRow({ phone, leadId }: { phone: any; leadId: string | number }) {
  const queryClient = useQueryClient()
  const setPrimaryMutation = useMutation({
    mutationFn: () => leadsApi.setPrimaryPhone(leadId, { phone_id: phone.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-contact-phones', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
    },
  })

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <Phone size={14} className="text-gray-400" />
        <span className="text-sm text-gray-700">{String(phone.phone)}</span>
        {phone.is_primary && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded">
            <Star size={10} className="fill-amber-500 text-amber-500" /> Primary
          </span>
        )}
      </div>
      {!phone.is_primary && (
        <button
          onClick={() => setPrimaryMutation.mutate()}
          disabled={setPrimaryMutation.isPending}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
        >
          Set Primary
        </button>
      )}
    </div>
  )
}

function MergeLeadModal({ open, onClose, leadId, leadName }: { open: boolean; onClose: () => void; leadId: string | number; leadName: string }) {
  const queryClient = useQueryClient()
  const [mergeLeadId, setMergeLeadId] = useState('')
  const [mergeLeadPublicId, setMergeLeadPublicId] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const { data: searchResults } = useQuery({
    queryKey: ['leads-search-merge', searchQuery],
    queryFn: () => leadsApi.list({ search: searchQuery, limit: '10' }),
    enabled: searchQuery.length >= 2,
  })

  const mergeMutation = useMutation({
    mutationFn: () => leadsApi.mergeLead(leadId, { merge_lead_id: parseInt(mergeLeadId) }),
    onSuccess: () => {
      setSuccess(true)
      setError('')
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-contact-emails', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-contact-phones', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lead-submissions', leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setTimeout(() => { onClose(); setSuccess(false); setMergeLeadId(''); setMergeLeadPublicId(''); setSearchQuery('') }, 1500)
    },
    onError: (err: Error) => setError(err.message),
  })

  const filteredResults = (searchResults?.leads || []).filter(l => l.id !== leadId)

  const displayMergeId = mergeLeadPublicId || mergeLeadId

  return (
    <Modal open={open} onClose={onClose} title="Merge Lead" size="md">
      <div className="space-y-4">
        {success && (
          <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg">Leads merged successfully.</div>
        )}
        {error && (
          <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search for lead to merge</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or phone..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {filteredResults.length > 0 && (
            <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
              {filteredResults.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { setMergeLeadId(String(l.id)); setMergeLeadPublicId((l as any).lead_id || ''); setSearchQuery(l.name) }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    String(l.id) === mergeLeadId ? 'bg-indigo-50' : ''
                  }`}
                >
                  <span className="font-medium text-gray-900">{l.name}</span>
                  <span className="text-gray-500 ml-2 font-mono text-xs">{(l as any).lead_id}</span>
                  {l.email && <span className="text-gray-400 ml-2">{l.email}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {mergeLeadId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              This will merge <strong>{displayMergeId}</strong> into <strong>{leadName}</strong>.
              All data from {displayMergeId} will be combined here. This cannot be undone.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => mergeMutation.mutate()}
            loading={mergeMutation.isPending}
            disabled={!mergeLeadId || mergeLeadId === String(leadId)}
          >
            <GitMerge size={14} /> Merge Leads
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function LeadNotesSection({ leadRef }: { leadRef: string | number }) {
  const queryClient = useQueryClient()
  const [newNote, setNewNote] = useState('')

  const { data: notesData } = useQuery({
    queryKey: ['lead-notes', leadRef],
    queryFn: () => leadsApi.getNotes(leadRef),
  })

  const addNoteMutation = useMutation({
    mutationFn: (content: string) => leadsApi.addNote(leadRef, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-notes', leadRef] })
      setNewNote('')
    },
  })

  const notes: any[] = notesData?.data || []

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes</h3>

      {/* Add note form */}
      <div className="mb-4">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        />
        <div className="flex justify-end mt-1.5">
          <Button
            size="sm"
            onClick={() => { if (newNote.trim()) addNoteMutation.mutate(newNote.trim()) }}
            loading={addNoteMutation.isPending}
            disabled={!newNote.trim()}
          >
            Add Note
          </Button>
        </div>
      </div>

      {/* Notes list (newest first, immutable) */}
      {notes.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-2">No notes yet</p>
      ) : (
        <div className="space-y-3">
          {notes.map((n: any) => (
            <div key={n.id} className={`rounded-lg p-3 ${n.source === 'meta_lead_form' ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
              {n.source === 'meta_lead_form' && (
                <div className="flex items-center gap-1.5 mb-2 text-xs text-blue-600 font-medium">
                  <Megaphone size={12} /> Meta Lead Form
                </div>
              )}
              <div className="text-sm text-gray-800 prose prose-sm max-w-none">
                <ReactMarkdown>{n.content}</ReactMarkdown>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <span className="font-medium text-gray-500">{n.created_by_name || (n.source === 'meta_lead_form' ? 'Meta Ads' : 'System')}</span>
                <span>&middot;</span>
                <span>{fmtDateTime(n.created_at)}</span>
                <span>({fmtRelative(n.created_at)})</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function LeadTaskModal({ open, onClose, leadId }: { open: boolean; onClose: () => void; leadId: number }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [form, setForm] = useState({
    title: '',
    description: '',
    task_type: 'follow_up',
    priority: 'medium',
    due_date: '',
    assigned_to: String(user?.id || 1),
    is_recurring: false,
    recurrence_rule: '',
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: open && (user?.role === 'admin' || user?.role === 'manager'),
  })

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['lead-tasks'] })
      setForm({ title: '', description: '', task_type: 'follow_up', priority: 'medium', due_date: '', assigned_to: String(user?.id || 1), is_recurring: false, recurrence_rule: '' })
      onClose()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: Record<string, unknown> = {
      title: form.title,
      task_type: form.task_type,
      priority: form.priority,
      lead_id: leadId,
      assigned_to: parseInt(form.assigned_to),
    }
    if (form.description) data.description = form.description
    if (form.due_date) data.due_date = istInputToUTC(form.due_date)
    if (form.is_recurring) {
      data.is_recurring = true
      data.recurrence_rule = form.recurrence_rule
    }
    mutation.mutate(data)
  }

  const TYPE_OPTIONS = [
    { value: 'call', label: 'Call' },
    { value: 'email', label: 'Email' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'demo', label: 'Demo' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'custom', label: 'Custom' },
  ]

  const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ]

  const userOptions = (usersData?.users || []).map((u: any) => ({ value: String(u.id), label: u.full_name || u.username }))
  if (userOptions.length === 0) userOptions.push({ value: String(user?.id || 1), label: user?.full_name || user?.username || 'Me' })

  return (
    <Modal open={open} onClose={onClose} title="Add Task for Lead">
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{(mutation.error as Error).message}</div>}
        <Input label="Title *" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="e.g. Follow up on demo request" />
        <Textarea label="Description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional details..." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select label="Type" options={TYPE_OPTIONS} value={form.task_type} onChange={(e) => setForm(f => ({ ...f, task_type: e.target.value }))} />
          <Select label="Priority" options={PRIORITY_OPTIONS} value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} />
          <Input label="Due Date" type="datetime-local" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
          <Select label="Assign To" options={userOptions} value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="lead-task-recurring" checked={form.is_recurring} onChange={(e) => setForm(f => ({ ...f, is_recurring: e.target.checked }))} className="rounded" />
          <label htmlFor="lead-task-recurring" className="text-sm text-gray-700">Recurring task</label>
        </div>
        {form.is_recurring && (
          <Input label="Recurrence Rule" value={form.recurrence_rule} onChange={(e) => setForm(f => ({ ...f, recurrence_rule: e.target.value }))} placeholder="e.g. FREQ=WEEKLY;BYDAY=MO,WE,FR" />
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending}>Create Task</Button>
        </div>
      </form>
    </Modal>
  )
}

function CompanyLinkingSection({ lead, leadRef }: { lead: any; leadRef: string | number }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [roleEditing, setRoleEditing] = useState(false)

  const { data: searchResults } = useQuery({
    queryKey: ['company-search', searchQuery],
    queryFn: () => companiesApi.search(searchQuery),
    enabled: searchQuery.length >= 2,
  })

  const { data: companyData } = useQuery({
    queryKey: ['company', lead.company_id],
    queryFn: () => companiesApi.get(lead.company_id as number),
    enabled: !!lead.company_id,
  })

  const linkMutation = useMutation({
    mutationFn: (companyId: number) => leadsApi.update(leadRef, { company_id: companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
      setSearchQuery('')
      setShowSearch(false)
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: () => leadsApi.update(leadRef, { company_id: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
    },
  })

  const createAndLinkMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await companiesApi.create({ name })
      await leadsApi.update(leadRef, { company_id: res.data.id })
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const roleMutation = useMutation({
    mutationFn: (role: string) => leadsApi.update(leadRef, { company_role: role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadRef] })
      setRoleEditing(false)
    },
  })

  const ROLE_OPTIONS = [
    { value: '', label: 'No role set' },
    { value: 'Owner', label: 'Owner' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Decision Maker', label: 'Decision Maker' },
    { value: 'Technical', label: 'Technical' },
    { value: 'Procurement', label: 'Procurement' },
    { value: 'Other', label: 'Other' },
  ]

  const company = companyData?.data
  const companyResults: any[] = searchResults?.data || []

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Building2 size={14} className="text-gray-500" /> Company
      </h3>

      {lead.company_id ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/companies`)}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                {company?.name || lead.company || `Company #${lead.company_id}`}
              </button>
              {company?.industry && (
                <span className="text-xs text-gray-400">{company.industry}</span>
              )}
            </div>
            <button
              onClick={() => { if (confirm('Unlink this company from the lead?')) unlinkMutation.mutate() }}
              disabled={unlinkMutation.isPending}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
            >
              <Unlink size={12} /> Unlink
            </button>
          </div>

          {/* Company Role */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Role:</span>
            {roleEditing ? (
              <select
                value={lead.company_role || ''}
                onChange={(e) => roleMutation.mutate(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-indigo-500"
                autoFocus
                onBlur={() => setRoleEditing(false)}
              >
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <button
                onClick={() => setRoleEditing(true)}
                className="text-xs text-gray-700 hover:text-indigo-600 px-1.5 py-0.5 bg-gray-100 rounded hover:bg-indigo-50 transition-colors"
              >
                {lead.company_role || 'Set role...'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {lead.company && !showSearch && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-gray-700">{lead.company} <span className="text-xs text-gray-400">(text only, not linked)</span></span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => createAndLinkMutation.mutate(lead.company as string)}
                  disabled={createAndLinkMutation.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors disabled:opacity-50"
                >
                  <Plus size={12} /> Create Company
                </button>
                <button
                  onClick={() => setShowSearch(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                >
                  <Search size={12} /> Link Existing
                </button>
              </div>
            </div>
          )}

          {(!lead.company || showSearch) && (
            <div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for a company to link..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {companyResults.length > 0 && (
                <div className="mt-1.5 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {companyResults.map((c: any) => (
                    <button
                      key={c.id}
                      onClick={() => linkMutation.mutate(c.id)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="font-medium text-gray-900">{c.name}</span>
                      {c.industry && <span className="text-gray-400 ml-2">{c.industry}</span>}
                      {c.city && <span className="text-gray-400 ml-2">{c.city}</span>}
                    </button>
                  ))}
                </div>
              )}
              {searchQuery.length >= 2 && companyResults.length === 0 && (
                <p className="text-xs text-gray-400 mt-1.5 px-1">No companies found. Try a different search or create a new one.</p>
              )}
              {showSearch && (
                <button onClick={() => { setShowSearch(false); setSearchQuery('') }} className="text-xs text-gray-500 hover:text-gray-700 mt-2">
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CallTranscript({ path }: { path: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['call-transcript', path],
    queryFn: async () => {
      const { api } = await import('../../api/client')
      return api.get<{ data: { transcript: string | null; transcript_status: string | null; transcribed_at: string | null } }>(path)
    },
  })
  const t = data?.data
  return (
    <div className="px-6 pb-4">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Transcript</span>
          {t?.transcribed_at && <span className="text-[10px] text-gray-400">Machine transcript · {fmtDateTime(t.transcribed_at)}</span>}
        </div>
        {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
        {error && <p className="text-xs text-red-600">Couldn't load transcript</p>}
        {t && !t.transcript && <p className="text-xs text-gray-400">No speech detected in this recording.</p>}
        {t?.transcript && <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">{t.transcript}</p>}
      </div>
    </div>
  )
}

const MAX_RECORDING_BYTES = 50 * 1024 * 1024
const RECORDING_ACCEPT = 'audio/*,.m4a,.amr,.opus,.3gp,.aac'

/** "Add recording" for a manual call log logged without one (attach once). */
function AddCallRecording({ leadRef, callId }: { leadRef: string | number; callId: number }) {
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File | undefined) => {
    if (!file) return
    if (file.size > MAX_RECORDING_BYTES) {
      setError('Recording must be 50 MB or smaller')
      return
    }
    setUploading(true)
    setError(null)
    try {
      const { api } = await import('../../api/client')
      const fd = new FormData()
      fd.append('file', file)
      await api.upload(`/leads/${leadRef}/calls/${callId}/recording`, fd)
      queryClient.invalidateQueries({ queryKey: ['lead'] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="mt-1.5">
      <label className={`inline-flex items-center gap-1 text-[11px] font-medium ${uploading ? 'text-gray-400' : 'text-indigo-600 hover:text-indigo-700 cursor-pointer'}`}>
        <Upload size={11} />
        {uploading ? 'Uploading recording…' : 'Add recording'}
        <input
          type="file"
          accept={RECORDING_ACCEPT}
          className="hidden"
          disabled={uploading}
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = '' }}
        />
      </label>
      {error && <p className="text-[11px] text-red-600 mt-0.5">{error}</p>}
    </div>
  )
}

function ManualCallLogSection({ leadRef, callLogs }: { leadRef: string | number; callLogs: any[] }) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    outcome: 'connected',
    direction: 'outbound',
    duration_minutes: '',
    call_date: toISTInputValue(),
    summary: '',
    next_action: '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [recordingFile, setRecordingFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const submitCallLog = async () => {
    setSubmitting(true)
    setUploadError(null)
    const data: Record<string, unknown> = {
      // The input is IST wall-clock time; the API stores UTC.
      call_date: istInputToUTC(form.call_date) ?? new Date().toISOString(),
      outcome: form.outcome,
      direction: form.direction,
      summary: form.summary || null,
      next_action: form.next_action || null,
    }
    if (form.duration_minutes) data.duration_minutes = parseInt(form.duration_minutes)

    try {
      const { api } = await import('../../api/client')
      const res = await api.post<{ data: { id: number } }>(`/leads/${leadRef}/calls`, data)
      if (recordingFile && res?.data?.id) {
        const fd = new FormData()
        fd.append('file', recordingFile)
        try {
          await api.upload(`/leads/${leadRef}/calls/${res.data.id}/recording`, fd)
        } catch (err) {
          // The call log is saved; only the recording failed. Keep the form open
          // with the error so the rep knows the recording didn't attach.
          queryClient.invalidateQueries({ queryKey: ['lead'] })
          setUploadError(`Call logged, but the recording failed to upload: ${err instanceof Error ? err.message : 'unknown error'}`)
          setRecordingFile(null)
          return
        }
      }
      queryClient.invalidateQueries({ queryKey: ['lead'] })
      setShowForm(false)
      setRecordingFile(null)
      setForm({ outcome: 'connected', direction: 'outbound', duration_minutes: '', call_date: toISTInputValue(), summary: '', next_action: '' })
    } catch (err) {
      console.error('Failed to log call', err)
    } finally {
      setSubmitting(false)
    }
  }

  const OUTCOMES = [
    { value: 'interacted', label: 'Interacted' },
    { value: 'connected', label: 'Connected' },
    { value: 'answered', label: 'Answered' },
    { value: 'voicemail', label: 'Voicemail' },
    { value: 'no_answer', label: 'No Answer' },
    { value: 'busy', label: 'Busy' },
  ]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Call Logs</h3>
        {!showForm && (
          <Button size="sm" variant="secondary" onClick={() => setShowForm(true)}>
            <Phone size={14} /> Log Call
          </Button>
        )}
      </div>

      {/* Add call log form */}
      {showForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Direction" options={[{ value: 'outbound', label: 'Outbound' }, { value: 'inbound', label: 'Inbound' }]} value={form.direction} onChange={(e) => setForm(f => ({ ...f, direction: e.target.value }))} />
            <Select label="Outcome" options={OUTCOMES} value={form.outcome} onChange={(e) => setForm(f => ({ ...f, outcome: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Call Date & Time" type="datetime-local" value={form.call_date} onChange={(e) => setForm(f => ({ ...f, call_date: e.target.value }))} />
            <Input label="Duration (minutes)" type="number" value={form.duration_minutes} onChange={(e) => setForm(f => ({ ...f, duration_minutes: e.target.value }))} placeholder="e.g. 15" />
          </div>
          <Textarea label="Call Notes" value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} placeholder="What was discussed? Key points, decisions, next steps..." />
          <Input label="Next Action" value={form.next_action} onChange={(e) => setForm(f => ({ ...f, next_action: e.target.value }))} placeholder="e.g. Send proposal by Friday" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Call Recording (optional)</label>
            <input
              type="file"
              accept={RECORDING_ACCEPT}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                setUploadError(f && f.size > MAX_RECORDING_BYTES ? 'Recording must be 50 MB or smaller' : null)
                setRecordingFile(f && f.size <= MAX_RECORDING_BYTES ? f : null)
              }}
              className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="text-[11px] text-gray-400 mt-1">You can also add it later from the call log. It's transcribed automatically within about 6 hours.</p>
          </div>
          {uploadError && <p className="text-xs text-red-600">{uploadError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setShowForm(false); setUploadError(null); setRecordingFile(null) }}>Cancel</Button>
            <Button size="sm" onClick={submitCallLog} loading={submitting}>Save Call Log</Button>
          </div>
        </div>
      )}

      {/* Call logs list (immutable, newest first) */}
      {callLogs.length === 0 && !showForm ? (
        <p className="text-xs text-gray-400 text-center py-2">No call logs yet</p>
      ) : (
        <div className="space-y-2">
          {callLogs.map((c) => (
            <div key={c.id as number} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span title={c.source === 'voip' ? 'VoIP call' : 'Manual entry'}>
                    {c.source === 'voip' ? (
                      <Phone size={12} className="text-blue-500" />
                    ) : (
                      <Pencil size={12} className="text-gray-400" />
                    )}
                  </span>
                  <span className="text-xs text-gray-500">{fmtDateTime(c.call_date)}</span>
                  {c.direction && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${c.direction === 'inbound' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                      {c.direction === 'inbound' ? 'IN' : 'OUT'}
                    </span>
                  )}
                  {c.created_by_name && (
                    <>
                      <span className="text-gray-300">&middot;</span>
                      <span className="text-xs text-gray-400">by {String(c.created_by_name)}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.outcome as string} />
                  {c.duration_minutes != null && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{String(c.duration_minutes)} min</span>
                  )}
                </div>
              </div>
              {c.summary && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{String(c.summary)}</p>}
              {c.next_action && <p className="text-xs text-indigo-600 mt-1.5">Next: {String(c.next_action)}</p>}
              {c.source !== 'voip' && !c.voip_call_id && (
                c.has_manual_recording
                  ? <p className="text-[11px] text-gray-400 mt-1.5">Recording attached{c.transcript_status === 'completed' ? ' · transcribed' : ''}</p>
                  : <AddCallRecording leadRef={leadRef} callId={c.id as number} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CallPulseResponsesSection({ responses, leadRef }: { responses: any[]; leadRef: string | number }) {
  const queryClient = useQueryClient()
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [resumeId, setResumeId] = useState<number | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (id: number) => playbookApi.deleteResponseSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-pulse-responses', leadRef] })
    },
  })

  if (responses.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">CallPulse Responses</h3>
      <div className="space-y-2">
        {responses.map((rs: any) => (
          <div key={rs.id} className="bg-gray-50 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedId(expandedId === rs.id ? null : rs.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{rs.playbook_name || 'Playbook'}</span>
                {rs.status === 'draft' && <Badge variant="warning">Draft</Badge>}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                {rs.updated_at && <span>Updated {fmtDateTime(rs.updated_at)}</span>}
                {!rs.updated_at && <span>{fmtDateTime(rs.submitted_at || rs.created_at)}</span>}
              </div>
            </button>

            {/* Draft actions */}
            {expandedId === rs.id && rs.status === 'draft' && (
              <div className="px-4 py-2 flex items-center gap-2 border-t border-gray-200">
                <button
                  onClick={() => setResumeId(rs.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-xs font-medium rounded-lg transition-colors"
                >
                  <RotateCcw size={12} /> Resume
                </button>
                <button
                  onClick={() => {
                    if (confirm('Delete this draft? This cannot be undone.')) {
                      deleteMutation.mutate(rs.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-red-500 hover:bg-red-50 text-xs font-medium rounded-lg transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            )}

            {/* Responses */}
            {expandedId === rs.id && rs.responses && (
              <div className="px-4 pb-3 space-y-2 border-t border-gray-200 pt-3">
                {(rs.responses || []).map((r: any, idx: number) => (
                  <div key={r.id || idx}>
                    <p className="text-xs font-medium text-gray-500">{r.question_text || `Question ${idx + 1}`}</p>
                    <p className="text-sm text-gray-800 mt-0.5">
                      {r.answer_text || (r.answer_json ? (() => { try { return JSON.parse(r.answer_json).join(', ') } catch { return r.answer_json } })() : '—')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Resume modal — opens CallPulse with existing draft */}
      {resumeId && (
        <CallPulseModal
          leadRef={leadRef}
          playbookId={responses.find((rs: any) => rs.id === resumeId)?.playbook_id}
          resumeResponseSetId={resumeId}
          onClose={() => {
            setResumeId(null)
            queryClient.invalidateQueries({ queryKey: ['lead-pulse-responses', leadRef] })
          }}
        />
      )}
    </div>
  )
}

// ---- Edit History (admin/manager only) ----

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', email: 'Email', phone_number: 'Phone Number',
  company: 'Company', title: 'Title', industry: 'Industry',
  company_size: 'Company Size', website: 'Website',
  business_type: 'Business Type', lead_source: 'Lead Source',
  source: 'Source', notes: 'Notes', stage: 'Stage',
  status: 'Status', company_role: 'Company Role',
}

function humanizeField(field: string): string {
  return FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)'
  if (typeof value === 'string' && value.trim() === '') return '(empty)'
  return String(value)
}

function EditHistorySection({ leadRef }: { leadRef: string | number }) {
  const [expanded, setExpanded] = useState(false)

  const { data } = useQuery({
    queryKey: ['lead-edit-history', leadRef],
    queryFn: () => leadsApi.getEditHistory(leadRef),
    enabled: !!leadRef,
  })

  const entries = data?.data || []
  if (entries.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors rounded-xl"
      >
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">Edit History</h3>
          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{entries.length}</span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-3">
          {entries.map((entry: any) => (
            <div key={entry.id} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {entry.edited_by_name || `User #${entry.edited_by}`}
                </span>
                <span className="text-xs text-gray-400">{fmtDateTime(entry.edited_at)}</span>
              </div>
              <div className="space-y-1.5">
                {Object.entries(entry.changes as Record<string, { old: unknown; new: unknown }>).map(([field, change]) => (
                  <div key={field} className="flex items-start gap-2 text-xs">
                    <span className="text-gray-500 font-medium min-w-[100px] shrink-0">{humanizeField(field)}</span>
                    <span className="font-mono text-red-600 bg-red-50 px-1.5 py-0.5 rounded line-through break-all">
                      {formatValue(change.old)}
                    </span>
                    <span className="text-gray-400 shrink-0">&rarr;</span>
                    <span className="font-mono text-green-700 bg-green-50 px-1.5 py-0.5 rounded break-all">
                      {formatValue(change.new)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---- Timeline Card (unified activity feed item) ----

function TimelineCard({ item }: { item: {
  type: 'call' | 'note' | 'callpulse' | 'stage'
  date: string
  title: string
  detail: string
  actor: string
  icon: typeof Phone
  color: string
  borderColor: string
  raw: any
}}) {
  const [expanded, setExpanded] = useState(false)
  const Icon = item.icon

  const bgColors: Record<string, string> = {
    call: 'bg-blue-50',
    note: 'bg-amber-50',
    callpulse: 'bg-purple-50',
    stage: 'bg-green-50',
  }

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 border-l-4 ${item.borderColor} cursor-pointer hover:shadow-sm transition-shadow`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full ${bgColors[item.type]} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon size={14} className={item.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{item.title}</span>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {item.type === 'callpulse' ? 'CallPulse' : item.type}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
            <span>{fmtDateTime(item.date)}</span>
            <span className="text-gray-300">|</span>
            <span>{fmtRelative(item.date)}</span>
            {item.actor && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500">{item.actor}</span>
              </>
            )}
          </div>
          {!expanded && item.detail && (
            <p className="text-xs text-gray-500 mt-1 truncate">{item.detail}</p>
          )}
        </div>
        <div className="flex-shrink-0 mt-1">
          {item.detail && (
            expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && item.detail && (
        <div className="px-4 pb-3 ml-11">
          {item.type === 'note' ? (
            <div className="text-sm text-gray-700 prose prose-sm max-w-none">
              <ReactMarkdown>{item.detail}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.detail}</p>
          )}

          {/* Extra detail for call logs */}
          {item.type === 'call' && item.raw.next_action && (
            <p className="text-xs text-indigo-600 mt-1.5">Next: {String(item.raw.next_action)}</p>
          )}

          {/* Extra detail for CallPulse */}
          {item.type === 'callpulse' && item.raw.responses && (
            <div className="mt-2 space-y-1.5">
              {(item.raw.responses || []).map((r: any, idx: number) => (
                <div key={r.id || idx}>
                  <p className="text-xs font-medium text-gray-500">{r.question_text || `Question ${idx + 1}`}</p>
                  <p className="text-xs text-gray-700 mt-0.5">
                    {r.answer_text || (r.answer_json ? (() => { try { return JSON.parse(r.answer_json).join(', ') } catch { return r.answer_json } })() : '--')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expanded detail for stage changes (show badges) */}
      {expanded && item.type === 'stage' && (
        <div className="px-4 pb-3 ml-11 flex items-center gap-3">
          {item.raw.from_stage ? (
            <StageBadge stage={item.raw.from_stage} />
          ) : (
            <span className="text-xs text-gray-400 italic">Created</span>
          )}
          <span className="text-gray-400">&rarr;</span>
          <StageBadge stage={item.raw.to_stage} />
        </div>
      )}
    </div>
  )
}

// ---- Paginated Section (show first N items with show more/less toggle) ----

function PaginatedSection({ title, items, limit, renderItem }: {
  title: string
  items: any[]
  limit: number
  renderItem: (item: any, index: number) => React.ReactNode
}) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? items : items.slice(0, limit)
  const hasMore = items.length > limit

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <span className="text-xs text-gray-400">{items.length} total</span>
      </div>
      <div className="space-y-2">
        {visible.map((item, i) => renderItem(item, i))}
      </div>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 w-full py-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          {showAll ? (
            <>
              <ChevronUp size={12} /> Show less
            </>
          ) : (
            <>
              <ChevronDown size={12} /> Show all {items.length} items
            </>
          )}
        </button>
      )}
    </div>
  )
}

function ScoringInputsSection({ lead }: { lead: any }) {
  let attrs: Record<string, unknown> | null = null
  try {
    const raw = lead?.lead_attributes
    if (raw && typeof raw === 'object') attrs = raw
    else if (raw && typeof raw === 'string') attrs = JSON.parse(raw)
  } catch {
    return null
  }
  if (!attrs || Object.keys(attrs).length === 0) return null
  const entries = Object.entries(attrs).filter(([k]) => !k.endsWith('_delta_flagged'))
  if (entries.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-xs font-medium text-gray-500 uppercase mb-3">Scoring Inputs (CallPulse)</h3>
      <div className="space-y-1.5">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{key.startsWith('cpq_') ? key : key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</span>
            <span className="font-medium text-gray-900">{String(value)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
